import React from 'react';
import { Box, Typography } from '@mui/material';

const GOLD = '#d4af37';

export default function LoadingScreen() {
  return (
    <Box
      sx={{
        position: 'fixed', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(ellipse 120% 80% at 50% 40%, #1a150e 0%, #0a0806 50%, #050403 100%)',
        zIndex: 9999,
        overflow: 'hidden',
        animation: 'screenFadeIn 0.4s ease-out',
        '@keyframes screenFadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      {/* ── Ambient floating particles ── */}
      {[...Array(12)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: 3 + (i % 3) * 2,
            height: 3 + (i % 3) * 2,
            borderRadius: '50%',
            bgcolor: `${GOLD}${20 + (i % 4) * 10}`,
            left: `${8 + (i * 7.5) % 85}%`,
            top: `${10 + (i * 13) % 80}%`,
            animation: `float_${i % 3} ${3 + (i % 3) * 2}s ease-in-out infinite`,
            animationDelay: `${i * 0.3}s`,
            '@keyframes float_0': {
              '0%, 100%': { transform: 'translateY(0) scale(1)', opacity: 0.3 },
              '50%': { transform: 'translateY(-20px) scale(1.5)', opacity: 0.7 },
            },
            '@keyframes float_1': {
              '0%, 100%': { transform: 'translate(0, 0) scale(1)', opacity: 0.2 },
              '50%': { transform: 'translate(10px, -15px) scale(1.3)', opacity: 0.6 },
            },
            '@keyframes float_2': {
              '0%, 100%': { transform: 'translate(0, 0) scale(0.8)', opacity: 0.4 },
              '50%': { transform: 'translate(-8px, -25px) scale(1.4)', opacity: 0.8 },
            },
          }}
        />
      ))}

      {/* ── Radial gold glow behind logo ── */}
      <Box sx={{
        position: 'absolute',
        width: 400, height: 400, borderRadius: '50%',
        background: `radial-gradient(circle, ${GOLD}12 0%, ${GOLD}06 35%, transparent 65%)`,
        animation: 'glowPulse 3s ease-in-out infinite',
        '@keyframes glowPulse': {
          '0%, 100%': { transform: 'scale(0.9)', opacity: 0.6 },
          '50%': { transform: 'scale(1.15)', opacity: 1 },
        },
      }} />

      {/* ── Main content ── */}
      <Box sx={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', position: 'relative', zIndex: 2,
      }}>

        {/* Temple logo with rings */}
        <Box sx={{ position: 'relative', mb: 4 }}>
          {/* Outer rotating ring */}
          <Box sx={{
            position: 'absolute',
            top: -18, left: -18, right: -18, bottom: -18,
            borderRadius: '50%',
            border: `1.5px solid ${GOLD}20`,
            animation: 'spinSlow 12s linear infinite',
            '@keyframes spinSlow': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            '&::before': {
              content: '""', position: 'absolute',
              top: -3, left: '50%', transform: 'translateX(-50%)',
              width: 6, height: 6, borderRadius: '50%',
              bgcolor: GOLD, boxShadow: `0 0 10px ${GOLD}`,
            },
          }} />

          {/* Middle pulsing ring */}
          <Box sx={{
            position: 'absolute',
            top: -10, left: -10, right: -10, bottom: -10,
            borderRadius: '50%',
            border: `1px solid ${GOLD}15`,
            animation: 'ringPulse 2.5s ease-in-out infinite',
            '@keyframes ringPulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 0.4 },
              '50%': { transform: 'scale(1.06)', opacity: 0.8 },
            },
          }} />

          {/* Inner counter-rotating ring */}
          <Box sx={{
            position: 'absolute',
            top: -4, left: -4, right: -4, bottom: -4,
            borderRadius: '50%',
            border: `1px dashed ${GOLD}25`,
            animation: 'spinReverse 8s linear infinite',
            '@keyframes spinReverse': {
              from: { transform: 'rotate(360deg)' },
              to: { transform: 'rotate(0deg)' },
            },
          }} />

          {/* Logo circle */}
          <Box sx={{
            width: 90, height: 90,
            borderRadius: '50%',
            background: `linear-gradient(145deg, ${GOLD}30, ${GOLD}08)`,
            border: `2px solid ${GOLD}50`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 40px ${GOLD}20, inset 0 0 20px ${GOLD}10`,
            animation: 'logoBreathe 3s ease-in-out infinite',
            '@keyframes logoBreathe': {
              '0%, 100%': { boxShadow: `0 0 40px ${GOLD}20, inset 0 0 20px ${GOLD}10` },
              '50%': { boxShadow: `0 0 60px ${GOLD}35, inset 0 0 30px ${GOLD}18` },
            },
          }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/assets/icon_ohm_2.png`}
              alt="Om"
              sx={{
                width: 52, height: 52,
                objectFit: 'contain',
                filter: 'brightness(1.1) drop-shadow(0 0 8px rgba(212,175,55,0.4))',
              }}
            />
          </Box>
        </Box>

        {/* Temple name */}
        <Typography sx={{
          fontWeight: 800,
          fontSize: { xs: '1.1rem', sm: '1.3rem' },
          letterSpacing: 1,
          background: `linear-gradient(135deg, #fff 30%, ${GOLD} 70%, #e5c158 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textAlign: 'center',
          mb: 0.8,
          animation: 'textReveal 0.8s 0.3s cubic-bezier(0.16,1,0.3,1) both',
          '@keyframes textReveal': {
            from: { opacity: 0, transform: 'translateY(12px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}>
          Sri Kotta Kovilakam Temple
        </Typography>

        {/* Subtitle */}
        <Typography sx={{
          fontSize: '0.65rem',
          color: 'rgba(255,255,255,0.3)',
          fontWeight: 600,
          letterSpacing: 4,
          textTransform: 'uppercase',
          mb: 4,
          animation: 'textReveal 0.8s 0.5s cubic-bezier(0.16,1,0.3,1) both',
        }}>
          Muchukunnu Kshethram
        </Typography>

        {/* Loading bar */}
        <Box sx={{
          width: 160, height: 2, borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.06)',
          overflow: 'hidden', position: 'relative',
          animation: 'textReveal 0.8s 0.7s cubic-bezier(0.16,1,0.3,1) both',
        }}>
          <Box sx={{
            position: 'absolute', top: 0, left: 0,
            height: '100%', width: '40%',
            borderRadius: 2,
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            animation: 'barSlide 1.5s ease-in-out infinite',
            '@keyframes barSlide': {
              '0%': { left: '-40%' },
              '100%': { left: '100%' },
            },
          }} />
        </Box>

        {/* Loading text */}
        <Typography sx={{
          mt: 2,
          fontSize: '0.7rem',
          color: `${GOLD}80`,
          fontWeight: 600,
          letterSpacing: 3,
          textTransform: 'uppercase',
          animation: 'loadFlicker 2s ease-in-out infinite',
          '@keyframes loadFlicker': {
            '0%, 100%': { opacity: 0.4 },
            '50%': { opacity: 1 },
          },
        }}>
          Loading
        </Typography>
      </Box>

      {/* ── Bottom ornamental divider ── */}
      <Box sx={{
        position: 'absolute', bottom: 40,
        display: 'flex', alignItems: 'center', gap: 1.5,
        animation: 'textReveal 0.8s 0.9s cubic-bezier(0.16,1,0.3,1) both',
      }}>
        <Box sx={{
          width: 40, height: 1,
          background: `linear-gradient(90deg, transparent, ${GOLD}40)`,
        }} />
        <Box sx={{
          width: 5, height: 5, borderRadius: '50%',
          bgcolor: `${GOLD}50`,
          boxShadow: `0 0 8px ${GOLD}30`,
        }} />
        <Box sx={{
          width: 40, height: 1,
          background: `linear-gradient(90deg, ${GOLD}40, transparent)`,
        }} />
      </Box>
    </Box>
  );
}
